/**
 * Module dependencies.
 */

import { parsePhoneNumber as parsePhoneLib, isValidPhoneNumber } from 'libphonenumber-js';

/**
 * `ParsedPhone` type.
 */

export type ParsedPhone = {
  dialCode: string;
  phone: string;
};

/**
 * Parse phone number from international format.
 *
 * Extracts dial code and phone number from a string like "+351912345678"
 *
 * @param value - The phone number in international format (e.g., "+351912345678")
 * @returns Object with dialCode and phone, or null if invalid
 */

export function parsePhoneNumber(value: string): ParsedPhone | null {
  if (!value || typeof value !== 'string') {
    return null;
  }

  // Try to parse with libphonenumber-js
  try {
    const parsed = parsePhoneLib(value);

    if (parsed) {
      return {
        dialCode: parsed.countryCallingCode,
        phone: parsed.nationalNumber
      };
    }
  } catch (error) {
    // Fall through to regex parsing
  }

  // Fallback: try regex extraction
  // Matches format: +{1-4 digits}{rest}
  const match = value.match(/^\+?(\d{1,4})(.+)$/);

  if (match) {
    return {
      dialCode: match[1],
      phone: match[2].replace(/\s/g, '') // Remove spaces
    };
  }

  return null;
}

/**
 * Format phone number to international format.
 *
 * Combines dial code and phone number into format "+351912345678"
 *
 * @param dialCode - The country dial code (e.g., "351")
 * @param phone - The phone number without country code (e.g., "912345678")
 * @returns Formatted phone number with + prefix
 */

export function formatPhoneNumber(dialCode: string, phone: string): string {
  if (!dialCode || !phone) {
    return '';
  }

  // Remove any non-digit characters
  const cleanDialCode = dialCode.replace(/\D/g, '');
  const cleanPhone = phone.replace(/\D/g, '');

  return `+${cleanDialCode}${cleanPhone}`;
}

/**
 * Validate phone number for a specific country.
 *
 * Uses libphonenumber-js to validate if the phone number is valid
 * for the given country code.
 *
 * @param phone - The complete phone number in international format (e.g., "+351912345678")
 * @param countryCode - The ISO 2-letter country code (e.g., "PT")
 * @returns true if valid, false otherwise
 */

export function validatePhoneNumber(phone: string, countryCode?: string): boolean {
  if (!phone) {
    return false;
  }

  try {
    if (countryCode) {
      return isValidPhoneNumber(phone, countryCode as any);
    }

    return isValidPhoneNumber(phone);
  } catch (error) {
    return false;
  }
}

/**
 * Extract dial code from full phone number.
 *
 * Attempts to extract the dial code from a complete phone number.
 * Useful for parsing values in edit mode.
 *
 * @param fullNumber - Complete phone number (e.g., "+351912345678" or "351912345678")
 * @returns Object with dialCode and phone, or null if cannot parse
 */

export function extractDialCode(fullNumber: string): ParsedPhone | null {
  return parsePhoneNumber(fullNumber);
}

/**
 * Find dial code ID from dial code number.
 *
 * Searches for a DialCode object by its dialCode number.
 *
 * @param dialCodes - Array of DialCode objects
 * @param dialCodeNumber - The dial code number to search for (e.g., "351")
 * @returns The ID of the matching DialCode, or undefined if not found
 */

export function findDialCodeId(
  dialCodes: Array<{ id: string; dialCode: string }>,
  dialCodeNumber: string
): string | undefined {
  // Remove '+' prefix for comparison
  const cleanDialCodeNumber = dialCodeNumber.replace(/^\+/, '');

  const found = dialCodes.find(item => {
    const cleanItemDialCode = item.dialCode.replace(/^\+/, '');
    return cleanItemDialCode === cleanDialCodeNumber;
  });

  return found?.id;
}

/**
 * Get dial code number from dial code ID.
 *
 * Retrieves the dialCode number from a DialCode object by its ID.
 *
 * @param dialCodes - Array of DialCode objects
 * @param dialCodeId - The ID of the DialCode
 * @returns The dial code number, or undefined if not found
 */

export function getDialCodeNumber(
  dialCodes: Array<{ id: string; dialCode: string }>,
  dialCodeId: string
): string | undefined {
  const found = dialCodes.find(item => item.id === dialCodeId);

  // Return dial code without '+' prefix (formatPhoneNumber adds it)
  return found?.dialCode.replace(/^\+/, '');
}
